import { watch, type Ref } from 'vue';
import type { IdentifySubscriberRequestDto } from '@peanuds/shared';
import { useAuth0 } from '@auth0/auth0-vue';
import i18next from 'i18next';

import { useNovuClient } from './useNovuClient';
import { useNotificationsStore } from '../store/notifications.store';

/**
 * Manages the complete Novu initialization flow on login.
 *
 * @param isLoggedIn - Reactive boolean indicating login status (e.g., from useAuthSessionStore)
 * @param auth0 - Auth0 context from useAuth0() (for accessing user data)
 */
export const useNotificationsInitialization = (
  isLoggedIn: Ref<boolean>,
  auth0: ReturnType<typeof useAuth0>,
) => {
  const notificationsStore = useNotificationsStore();
  const { initialize: initializeNovu, disconnect: disconnectNovu } = useNovuClient();

  watch(
    () => ({
      loggedIn: isLoggedIn.value,
      user: auth0.user?.value,
      isLoading: auth0.isLoading.value,
    }),
    async (state) => {
      if (!state.loggedIn) {
        disconnectNovu();
        return;
      }

      if (state.isLoading || !state.user) {
        return;
      }

      const bootstrapResult = await initializeNovu();

      if (bootstrapResult !== 'subscriber-missing') {
        return;
      }

      const { sub, email, given_name, family_name, phone_number, picture } = state.user;

      if (!sub) {
        console.error('No subscriber ID found');
        return;
      }

      const subscriberData: IdentifySubscriberRequestDto = {
        subscriberId: sub,
        email,
        firstName: given_name,
        lastName: family_name,
        phone: phone_number,
        avatar: picture,
        locale: i18next.language || undefined,
      };

      try {
        const subscriber = await notificationsStore.identifySubscriber({ body: subscriberData });

        if (!subscriber) {
          return;
        }
      } catch (error) {
        console.error('Failed to identify subscriber:', error);
        return;
      }

      // Retry bootstrap after creating the subscriber server-side.
      void initializeNovu();
    },
    { immediate: true },
  );
};
